/**
 * reportMarkdown — the shared markdown rendering surface for BARK's
 * report-grade documents.
 *
 * This module is the single source of truth for how authored markdown renders:
 * the citation/online-support preprocessors and the ReactMarkdown component
 * overrides (citation chips, tables, headings, lists, code, …). It is consumed
 * by BOTH the research report (`ReportView`) and the documentation workspace
 * surface (`DocumentationView`) so the two can never drift — formatting,
 * citation chips, and table/heading styling are identical everywhere a
 * BARK-authored document is shown.
 *
 * What stays OUT of here (report-only): the BAR-192 grounding overlays
 * (`GroundingFlag`, the amber/danger paragraph highlights). `ReportView`
 * spreads `buildBaseMarkdownComponents(...)` and overrides only `p`/`li`
 * with its grounding-aware variants, so the base plain renderers here are what
 * documentation uses directly.
 */

import React from 'react';
import { Globe2, Landmark } from 'lucide-react';
import type { Components } from 'react-markdown';
import { DictMarker, DictFinding } from '../../types/workspaceState';
import type { OnlineSupport as OnlineSupportItem } from '../../types/online_support';
import { isSafeUrl } from '../../utils/urlSafety';

/**
 * Transform citation markers [label]{ref:marker_id} into markdown links
 * that ReactMarkdown can render with a custom `a` component.
 */
export function preprocessCitations(content: string): string {
  return content.replace(
    /\[([^\]]+)\]\{ref:([^}]+)\}/g,
    '[$1](#cite:$2)'
  );
}

/**
 * BAR-39: transform OnlineSupport references [label]{online:online_id}
 * into markdown links that ReactMarkdown can parse. The `a` component
 * override renders these as OnlineSupport chips (globe icon, neutral
 * colour) rather than regulatory-citation chips.
 */
export function preprocessOnlineRefs(content: string): string {
  return content.replace(
    /\[([^\]]+)\]\{online:([^}]+)\}/g,
    '[$1](#online:$2)',
  );
}

/**
 * BAR-39: catch *bare* OnlineSupport references — `{online:online_id}`
 * with no preceding `[label]`. Give it a sentinel label so it renders as a
 * globe chip just like the labelled form; the `a`-override then swaps the
 * sentinel for the OnlineSupport's real name when it resolves. Runs AFTER
 * preprocessOnlineRefs, so anything matching here is genuinely label-less.
 */
export const BARE_ONLINE_LABEL = 'Online source';
export function preprocessBareOnlineRefs(content: string): string {
  return content.replace(
    /(?<!\])\{online:([^}]+)\}/g,
    `[${BARE_ONLINE_LABEL}](#online:$1)`,
  );
}

/**
 * WS2: transform documentation requirement references `{req:REQ-XXX}` (bare, or
 * with a preceding `[label]` that is discarded) into markdown links the `a`
 * override renders as an INHERITED-CITATION chip. The documentation workspace
 * cites upstream requirements, not regulation directly; the chip resolves the
 * requirement's first-order citation from the linked upstream requirements and
 * shows THAT (e.g. "CRR Art 92(1)(a)") — the regulation is inherited, never
 * re-typed by the doc author.
 */
export const BARE_REQ_LABEL = 'req';
export function preprocessRequirementRefs(content: string): string {
  return content
    // Labelled form: drop the label (the citation is inherited from the req).
    .replace(/\[[^\]]*\]\{req:([^}]+)\}/g, `[${BARE_REQ_LABEL}](#req:$1)`)
    // Bare form.
    .replace(/\{req:([^}]+)\}/g, `[${BARE_REQ_LABEL}](#req:$1)`);
}

/**
 * The full preprocessing chain a BARK document goes through before
 * ReactMarkdown sees it: citations → requirement refs → online refs → bare
 * online refs. Order matters (bare-online runs last so labelled refs are
 * already linked).
 */
export function preprocessDocumentMarkdown(content: string): string {
  return preprocessBareOnlineRefs(
    preprocessOnlineRefs(
      preprocessRequirementRefs(preprocessCitations(content)),
    ),
  );
}

/** Resolved upstream requirement backing a `{req:REQ-XXX}` chip. */
export interface LinkedRequirementInfo {
  /** Inherited first-order citation (first grounding finding's excerpt). */
  citation?: string;
  /** Whether the requirement carries grounding (renders accent vs. muted). */
  grounded?: boolean;
  /** The requirement statement (tooltip). */
  statement?: string;
  /** Human-readable source workspace name (tooltip). */
  sourceWorkspaceName?: string;
}

export interface BaseMarkdownOptions {
  /** Marker registry resolving [label]{ref:marker_id} → finding. */
  markers: Record<string, DictMarker>;
  /** Finding registry (source name / reference / category). */
  findings: Record<string, DictFinding>;
  /** OnlineSupport registry resolving {online:id} chips. */
  onlineSupport?: Record<string, OnlineSupportItem>;
  /** Click a regulatory citation chip → open the finding. */
  onCitationClick?: (findingId: string, markerId: string) => void;
  /** Click an OnlineSupport chip → open the online source. */
  onOnlineRefClick?: (onlineSupportId: string) => void;
  /** The finding currently selected in the side panel (chip highlight). */
  selectedFindingId?: string | null;
  /** The OnlineSupport currently selected in the side panel (chip highlight). */
  selectedOnlineSupportId?: string | null;
  /** WS2: linked upstream requirements resolving `{req:REQ-XXX}` → the inherited
   *  first-order citation (keyed by upper-cased REQ id). */
  linkedRequirements?: Record<string, LinkedRequirementInfo>;
  /** Click a requirement chip → open the linked upstream requirement. */
  onRequirementRefClick?: (requirementId: string) => void;
  /** The requirement currently selected in the side panel (chip highlight). */
  selectedRequirementId?: string | null;
}

/**
 * Build the ReactMarkdown `components` map shared by every BARK document
 * surface. The return type is annotated `Components` so each override's props
 * are contextually typed by react-markdown.
 *
 * `p` and `li` here are the PLAIN renderers (no grounding overlay).
 * `ReportView` spreads this map and replaces `p`/`li` with grounding-aware
 * variants; `DocumentationView` uses them as-is.
 */
export function buildBaseMarkdownComponents(
  opts: BaseMarkdownOptions,
): Components {
  const {
    markers,
    findings,
    onlineSupport,
    onCitationClick,
    onOnlineRefClick,
    selectedFindingId,
    selectedOnlineSupportId,
    linkedRequirements,
    onRequirementRefClick,
    selectedRequirementId,
  } = opts;

  const handleCitationClick = (href: string | undefined) => {
    if (!href?.startsWith('#cite:')) return;
    const markerId = href.slice(6);
    const marker = markers[markerId];
    if (marker && onCitationClick) {
      onCitationClick(marker.finding_id, markerId);
    }
  };

  return {
    a: ({ href, children }) => {
      if (href?.startsWith('#cite:')) {
        const markerId = href.slice(6);
        const marker = markers[markerId];
        const finding = marker ? findings[marker.finding_id] : null;
        // Input citations (workspace-sourced facts — institution profile,
        // upstream specs) render with a landmark icon + the neutral
        // online-support palette, so regulatory vs. input grounding is
        // scannable at a glance.
        const isInput = finding?.category === 'input';
        const tooltip = finding
          ? isInput
            ? `Input — ${finding.source_document_name || 'Workspace'} · ${finding.source_reference || ''}`
            : `${finding.source_document_name || 'Source'} — ${finding.source_reference || 'Unknown ref'}`
          : 'Citation';
        const isCiteSelected = !!marker && marker.finding_id === selectedFindingId;
        return (
          <button
            onClick={() => handleCitationClick(href)}
            title={tooltip}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 text-xs font-medium border rounded transition-colors cursor-pointer align-baseline ${
              isCiteSelected
                ? 'text-link bg-link-bg border-link'
                : isInput
                  ? 'text-fg-secondary bg-bg-warm border-border-default hover:bg-surface-sunken'
                  : 'text-accent bg-accent-tint border-accent-border hover:text-accent-hover'
            }`}
          >
            {isInput && (
              <Landmark size={11} strokeWidth={1.5} className="flex-shrink-0" />
            )}
            {children}
          </button>
        );
      }
      // BAR-39: OnlineSupport chip — `[label]{online:online_id}`.
      if (href?.startsWith('#online:')) {
        const onlineId = href.slice(8);
        const support = onlineSupport?.[onlineId];
        const tooltip = support
          ? `Online support — ${support.rationale || support.query}`
          : 'Online support (not a regulatory citation)';
        const childText = Array.isArray(children)
          ? children.join('')
          : typeof children === 'string'
            ? children
            : '';
        const isBareOnlineRef = childText === BARE_ONLINE_LABEL;
        const onlineLabel = isBareOnlineRef
          ? support?.rationale || support?.query || BARE_ONLINE_LABEL
          : children;
        const onlineLabelEl = isBareOnlineRef ? (
          <span className="truncate max-w-[18rem]">{onlineLabel}</span>
        ) : (
          onlineLabel
        );
        const isOnlineSelected = onlineId === selectedOnlineSupportId;
        const chipClass =
          'inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 text-xs font-medium border rounded align-baseline ' +
          (isOnlineSelected
            ? 'text-link bg-link-bg border-link'
            : 'text-fg-secondary bg-bg-warm border-border-default');
        if (onOnlineRefClick) {
          return (
            <button
              type="button"
              onClick={() => onOnlineRefClick(onlineId)}
              title={tooltip}
              className={`${chipClass} ${isOnlineSelected ? '' : 'hover:bg-surface-sunken'} transition-colors cursor-pointer`}
            >
              <Globe2 size={11} strokeWidth={1.5} className="flex-shrink-0" />
              {onlineLabelEl}
            </button>
          );
        }
        return (
          <span title={tooltip} className={chipClass}>
            <Globe2 size={11} strokeWidth={1.5} className="flex-shrink-0" />
            {onlineLabelEl}
          </span>
        );
      }
      // WS2: documentation requirement chip — `{req:REQ-XXX}`. Resolves against
      // the linked upstream requirements and renders the INHERITED first-order
      // citation (the doc never types the provision). Accent chip when grounded;
      // muted when the requirement is unresolved or carries no citation yet.
      if (href?.startsWith('#req:')) {
        const reqId = href.slice(5).toUpperCase();
        const info = linkedRequirements?.[reqId];
        // Option B: the chip surfaces the REQUIREMENT (the provenance / unit the
        // doc references), with its inherited first-order citation alongside —
        // "REQ-001 · CRR Art 395(1)". Clicking drills to the requirement, whose
        // detail holds the backing findings.
        const label = info?.citation ? `${reqId} · ${info.citation}` : reqId;
        // Accent when the requirement is a valid citation target (resolved +
        // grounded). A design requirement is grounded WITHOUT a citation, so
        // don't require `citation` here — otherwise a {req:} that passes the
        // ship gate would render muted (gate/UI mismatch).
        const grounded = !!info && info.grounded !== false;
        const tooltip = info
          ? `${reqId}${info.grounded === false ? ' (ungrounded)' : ''}` +
            (info.statement ? ` — ${info.statement}` : '') +
            (info.sourceWorkspaceName ? ` · ${info.sourceWorkspaceName}` : '')
          : `${reqId} — not found in linked upstream requirements`;
        const isReqSelected = reqId === selectedRequirementId;
        const chipClass =
          'inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 text-xs font-medium border rounded align-baseline ' +
          (isReqSelected
            ? 'text-link bg-link-bg border-link'
            : grounded
              ? 'text-accent bg-accent-tint border-accent-border hover:text-accent-hover'
              : 'text-fg-secondary bg-bg-warm border-border-default');
        const inner = (
          <>
            <Landmark size={11} strokeWidth={1.5} className="flex-shrink-0" />
            {label}
          </>
        );
        if (onRequirementRefClick) {
          return (
            <button
              type="button"
              onClick={() => onRequirementRefClick(reqId)}
              title={tooltip}
              className={`${chipClass} transition-colors cursor-pointer`}
            >
              {inner}
            </button>
          );
        }
        return (
          <span title={tooltip} className={chipClass}>
            {inner}
          </span>
        );
      }
      // Regular external link. `#cite:`/`#online:` are handled above; anything
      // reaching here is an arbitrary href from (possibly untrusted) markdown.
      // Only render a clickable anchor when the scheme is http/https — anything
      // else (javascript:, data:, file:, …) is rendered as plain text to
      // eliminate click-triggered XSS.
      if (!isSafeUrl(href)) {
        return <span>{children}</span>;
      }
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-accent hover:text-accent-hover underline">
          {children}
        </a>
      );
    },
    table: ({ children }) => (
      <table className="border-collapse border border-border-default my-3 text-sm w-full">
        {children}
      </table>
    ),
    th: ({ children }) => (
      <th className="border border-border-default px-3 py-2 bg-bg-warm font-semibold text-left text-fg-secondary">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="border border-border-default px-3 py-2 text-fg-secondary">
        {children}
      </td>
    ),
    h1: ({ children }) => <h1 className="text-lg font-bold text-fg mt-6 mb-3">{children}</h1>,
    h2: ({ children }) => <h2 className="text-base font-semibold text-fg mt-5 mb-2">{children}</h2>,
    h3: ({ children }) => <h3 className="text-sm font-semibold text-fg-secondary mt-4 mb-2">{children}</h3>,
    p: ({ children }) => (
      <p className="text-sm leading-relaxed text-fg-secondary mb-3">{children}</p>
    ),
    ul: ({ children }) => <ul className="list-disc list-outside pl-5 mb-3 space-y-1">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal list-outside pl-5 mb-3 space-y-1">{children}</ol>,
    li: ({ children }) => (
      <li className="text-sm text-fg-secondary leading-relaxed">{children}</li>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-3 border-border-default pl-4 my-3 text-fg-tertiary italic">
        {children}
      </blockquote>
    ),
    code: ({ children, className }) => {
      const isBlock = className?.includes('language-');
      if (isBlock) {
        return (
          <code className={`${className} text-sm`}>
            {children}
          </code>
        );
      }
      return (
        <code className="text-sm bg-bg-warm text-fg px-1.5 py-0.5 rounded">
          {children}
        </code>
      );
    },
    pre: ({ children }) => (
      <pre className="bg-bg-warm border border-border-default rounded-lg p-4 my-3 overflow-x-auto">
        {children}
      </pre>
    ),
    hr: () => <hr className="border-border-default my-6" />,
    strong: ({ children }) => <strong className="font-semibold text-fg">{children}</strong>,
    em: ({ children }) => <em className="italic text-fg-secondary">{children}</em>,
  };
}
